// https://developer.fastly.com/reference/io/#configuration
import type { ImageLoaderProps } from "next/image";

export type FastlyImageOptimizerParams = {
    auto?: "avif" | "webp";
    "bg-color"?: string;
    blur?: string;
    brightness?: string;
    canvas?: string;
    contrast?: string;
    crop?: string;
    disable?: "upscale";
    dpr?: string;
    enable?: "upscale";
    fit?: "cover" | "crop" | "bounds";
    format?:
        | "avif"
        | "bjpg"
        | "gif"
        | "jpg"
        | "pjpg"
        | "png"
        | "png8"
        | "webp"
        | "webpll"
        | "webply";
    frame?: "1";
    height?: string;
    level?: string;
    optimize?: "low" | "medium" | "high";
    orient?: "r" | "l" | "v" | "h" | "hv" | "vh" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8";
    pad?: string;
    precrop?: string;
    profile?: "baseline" | "main" | "high";
    quality?: string;
    "resize-filter"?:
        | "nearest"
        | "bilinear"
        | "linear"
        | "bicubic"
        | "cubic"
        | "lanczos2"
        | "lanczos3"
        | "lanczos";
    saturation?: string;
    sharpen?: string;
    trim?: string;
    "trim-color"?: string;
    width?: string;
};

const REWRITE_CDN_URL = process.env.NEXT_PUBLIC_APRIMO_REWRITE_URL;

export const aprimoLoader = (
    { src, width, quality = 80 }: ImageLoaderProps,
    imageOptimizerParams?: FastlyImageOptimizerParams
): string => {
    if (imageOptimizerParams?.auto === "avif" || imageOptimizerParams?.format === "avif") {
        process.env.CONTEXT !== "production" &&
            console.warn(
                "AVIF is a premium feature of Fastly's Image Optimizer and choosing it as an encoding format might increase your bill."
            );
    }

    const { width: _width, quality: _quality, ...restParams } = imageOptimizerParams ?? {};

    const params = new URLSearchParams({
        format: "auto",
        ...restParams,
        width: width.toString(),
        quality: quality?.toString() ?? "80",
    });

    const url = new URL(src);

    params.forEach((value, key) => {
        if (url.searchParams.has(key)) {
            url.searchParams.set(key, value);
            return;
        }
        url.searchParams.append(key, value);
    });

    const imageSrc = url.toString();

    const rewrittenSrc = REWRITE_CDN_URL
        ? imageSrc.replace(/https:\/\/p3\.aprimocdn\.net\/ecco\/?/g, `${REWRITE_CDN_URL}/`)
        : imageSrc;

    return `${rewrittenSrc}`;
};
